import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import FeedbackIcon from "@mui/icons-material/Feedback";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
// --- Importaciones de Firebase ---
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../services/firebase";

/**
 * Componente modal para enviar feedback - Accesible para todos los usuarios
 * Especialmente diseñado para ser amigable con personas con síndrome de Down
 */
const FeedbackModal = ({ open, onClose }) => {
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const theme = useTheme();

  // Reset estado cuando se abre el modal
  useEffect(() => {
    if (open) {
      setShowThankYou(false);
    }
  }, [open]);

  const handleTextChange = (event) => {
    setFeedbackText(event.target.value);
  };

  const handleSubmit = async () => {
    if (!feedbackText.trim()) {
      enqueueSnackbar("Por favor, escribe tu mensaje antes de enviar", {
        variant: "warning",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Añade el documento a la colección 'feedback'
      // IMPORTANTE: Permite envío de feedback anónimo (sin estar autenticado)
      await addDoc(collection(db, "feedback"), {
        // Guarda el ID y email del usuario solo si está autenticado
        userId: user ? user.uid : null,
        userEmail: user ? user.email : null,
        // El texto del feedback
        text: feedbackText,
        // La fecha y hora del servidor cuando se creó
        createdAt: serverTimestamp(),
        // Un estado inicial para poder gestionarlo en el dashboard
        status: "new",
        // La URL de la página desde donde se envió el feedback
        pageUrl: window.location.href,
        // Información adicional útil
        userAgent: navigator.userAgent,
      });

      // Muestra el snackbar
      enqueueSnackbar("¡Gracias por tu mensaje!", { variant: "success" });

      // Muestra la pantalla de agradecimiento
      setShowThankYou(true);
      setFeedbackText(""); // Limpia el campo para la próxima vez
    } catch (error) {
      console.error("Error al enviar feedback:", error);
      enqueueSnackbar(
        "Ha ocurrido un problema al enviar tu mensaje. Por favor, inténtalo de nuevo.",
        {
          variant: "error",
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Limpiar el texto al cerrar si no se envió
  const handleClose = () => {
    if (!isSubmitting) {
      setFeedbackText("");
    }
    if (showThankYou) {
      setShowThankYou(false);
    }
    onClose();
  };

  // Renderiza el contenido principal del modal
  const renderMainContent = () => {
    if (showThankYou) {
      return (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            px: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
          role="region"
          aria-label="Mensaje de agradecimiento"
        >
          <CheckCircleOutlineIcon
            color="success"
            sx={{ fontSize: 90, mb: 1 }}
            aria-hidden="true"
          />

          <Typography
            variant="h4"
            gutterBottom
            fontWeight="bold"
            color="primary"
            aria-live="polite"
            sx={{ fontSize: { xs: "1.75rem", sm: "2.25rem" } }}
          >
            ¡Muchas gracias por tu mensaje!
          </Typography>

          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,100,255,0.05)",
              border: `2px solid ${theme.palette.primary.main}`,
              width: "90%",
              maxWidth: "550px",
            }}
          >
            <Typography
              variant="h6"
              paragraph
              sx={{
                lineHeight: 1.6,
                fontSize: "1.25rem",
                letterSpacing: "0.02em",
              }}
            >
              Tu opinión es muy importante para nosotros y nos ayuda a mejorar
              VicDown para toda la comunidad.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: "1.1rem",
                lineHeight: 1.5,
              }}
            >
              Revisaremos tu mensaje lo antes posible y trabajaremos para
              implementar tus sugerencias.
            </Typography>
          </Paper>

          <Button
            variant="contained"
            color="primary"
            onClick={handleClose}
            size="large"
            sx={{
              mt: 2,
              minWidth: "160px",
              fontSize: "1.1rem",
              py: 1.5,
              borderRadius: 2,
            }}
            aria-label="Cerrar ventana de agradecimiento"
          >
            Cerrar
          </Button>
        </Box>
      );
    }

    return (
      <>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pb={1}>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <FeedbackIcon
                color="primary"
                sx={{ fontSize: 36 }}
                aria-hidden="true"
              />
              <Typography variant="h6" fontWeight="500" color="primary">
                Tu opinión nos ayuda a mejorar
              </Typography>
            </Box>

            <Typography
              variant="body1"
              sx={{
                fontSize: "1.05rem",
                lineHeight: 1.5,
                mb: 1,
              }}
            >
              Valoramos tu opinión. Ayúdanos a mejorar VicDown describiendo tu
              sugerencia o el problema que encontraste.
            </Typography>

            <TextField
              autoFocus
              margin="dense"
              id="feedback-text"
              label="Tu mensaje"
              type="text"
              fullWidth
              multiline
              rows={5}
              variant="outlined"
              value={feedbackText}
              onChange={handleTextChange}
              disabled={isSubmitting}
              placeholder="Escribe aquí tu sugerencia, idea o reporte de error..."
              aria-describedby="feedback-helper-text"
              inputProps={{
                "aria-label": "Escribe tu mensaje de sugerencia o reporte",
                maxLength: 1000,
              }}
              sx={{
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  fontSize: "1.05rem",
                },
              }}
            />
            <Typography
              id="feedback-helper-text"
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.85rem", mt: 0.5 }}
            >
              No necesitas estar registrado para enviar un mensaje. ¡Todas las
              opiniones son bienvenidas!
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: "16px 24px" }}>
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            color="secondary"
            size="large"
            aria-label="Cancelar y cerrar formulario"
            sx={{
              px: 2,
              fontSize: "1rem",
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            size="large"
            disabled={isSubmitting || !feedbackText.trim()}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
            aria-label="Enviar mensaje de feedback"
            sx={{
              px: 3,
              fontSize: "1rem",
              fontWeight: 500,
            }}
          >
            {isSubmitting ? "Enviando..." : "Enviar Mensaje"}
          </Button>
        </DialogActions>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="feedback-dialog-title"
      aria-describedby="feedback-dialog-description"
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 2,
        },
      }}
    >
      {!showThankYou && (
        <DialogTitle id="feedback-dialog-title">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" component="span">
              Enviar Sugerencia o Reporte
            </Typography>
            <IconButton
              aria-label="Cerrar"
              onClick={handleClose}
              disabled={isSubmitting}
              edge="end"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
      )}
      {renderMainContent()}
    </Dialog>
  );
};

export default FeedbackModal;
